import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, PRIMARY_OUTLET, Router } from '@angular/router';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { EventBusEvents } from '@siza-staff-care/event-bus-constants';
import { AppStateService, UserAccessService } from '@siza-staff-care/shared/app-state';
import {
	ApiErrorResult,
	ApiLoginCheckRequest,
	ApiLoginRequest,
	ApiLoginRequest_Constants,
	ApiLoginResult,
	ApiResendMultiFactorAuthenticationCodeRequest,
	ApiResetPasswordRequest,
	ApiResetPasswordRequest_Constants,
	ApiSessionForceRemoveNotification,
	KeyValuesPair,
	OptionsDto,
	StaffMasterLoginInfoDto,
	Types,
} from '@siza-staff-care/shared/data';
import { BusinessError } from '@siza-staff-care/shared/global-errors';
import { GlobalStoreRegistryStore } from '@siza-staff-care/shared/ui-components/store-registry';
import { Mutable, assertDefined, ensureDateTime } from '@siza-staff-care/shared/utils';
import { NgEventBus } from 'ng-event-bus';
import {
	BehaviorSubject,
	EMPTY,
	Observable,
	Subject,
	catchError,
	delay,
	exhaustMap,
	filter,
	finalize,
	merge,
	of,
	switchMap,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';
import { Global } from './global.service';
import { JadeService } from './jade.service';
import { MenuBuilderService } from './menu-builder.service';
import {
	addSecurityItemsToSecurityMap,
	buildEditorMap,
	buildSecurityMapFromMenuItems,
	flattenMenuItems,
} from './menu-routes/security-builders';
import { securityMappings } from './menu-routes/security-mappings';
import { NotificationsService } from './notifications';
import { SessionTimeoutService } from './session-timeout.service';
import { SessionService } from './session.service';

const ForceRemove = 19;

export const enum Modes {
	Login = 'login',
	LoginICare = 'loginICare',
	ClockIn = 'clockin',
	ClockOut = 'clockout',
	ResetPassword = 'resetPassword',
}

export enum UiMode {
	Login = 'login',
	LoginFromICare = 'loginFromICare',
	ResetPassword = 'resetPassword',
	ChangePassword = 'changePassword',
	ChangePasswordFromReset = 'changePasswordFromReset',
	SelectStaffMaster = 'selectStaffMaster',
	MultiFactorAuthentication = 'multiFactorAuthentication',
	TotpAuthentication = 'totpAuthentication',
}

type UiModeType =
	| UiMode.Login
	| UiMode.LoginFromICare
	| UiMode.ResetPassword
	| UiMode.ChangePassword
	| UiMode.SelectStaffMaster
	| UiMode.ChangePasswordFromReset
	| UiMode.MultiFactorAuthentication
	| UiMode.TotpAuthentication;

export interface AuthenticateParams {
	username: string;
	password: string;
	rememberUserName: boolean;
	mode: Modes;
	route: ActivatedRoute;
	additionalData?: object;
}

interface LoginCompleteParams {
	jwt: string;
	menuItems?: KeyValuesPair[];
	securityItems: KeyValuesPair[] | null;
	editorItems: KeyValuesPair[] | null;
	options: OptionsDto;
}

interface MultiStaffMasterLoginParams {
	username?: string;
	password?: string;
	newPassword?: string;
	rememberMe?: boolean;
	changingPasswordFromReset?: boolean;
	changingPasswordFromExpiry?: boolean;
}

interface ChangePasswordParameters {
	username: string;
	oldPassword: string;
	newPassword: string;
}

interface ResetPasswordParameters {
	username: string;
	emailAddress: string;
}

interface ChangePasswordFromResetParameters {
	id: string;
	username: string;
}

interface ChangePasswordFromResetCompleteParameters {
	newPassword: string;
	route: ActivatedRoute;
}

export interface CompleteClockingParameters {
	staffName: string;
	mode: Modes;
}

interface AuthState {
	inProgress: boolean;
	message: string;
	retainMessage?: boolean;
	suppressCheckUnsavedChanges?: boolean;
	fromICareUserName?: string;
	passwordResetId?: string;
	mode?: Modes;
	loggingOut?: boolean;
	loggingIn?: boolean;
	loginStage?: string;
	isOrchestrating?: boolean;
	windowsAuthenticationMode?: boolean;
	multiStaffMasters?: StaffMasterLoginInfoDto[];
	uiMode: UiModeType;
	multiStaffMasterLoginParams?: MultiStaffMasterLoginParams;
	route?: ActivatedRoute;
	changePasswordFromResetParams?: ChangePasswordFromResetParameters;
	chosenStaffMasterOid?: string;
	mfa?: {
		codeGeneratedAt: Date;
		key: string;
		expirySeconds: number;
		accessCodeOid: string;
		resending?: boolean;
		validating?: boolean;
	};
	totp?: {
		validating?: boolean;
		settingUp?: boolean;
	};
}

const defaultState: AuthState = {
	inProgress: false,
	message: '',
	fromICareUserName: '',
	passwordResetId: '',
	loggingOut: false,
	loggingIn: false,
	loginStage: '',
	isOrchestrating: false,
	multiStaffMasters: [],
	uiMode: UiMode.Login,
};

const whiteListRoutes = {
	'/staff/staff-alerts': securityMappings.Alerts,
	'/staff/staff-messages': securityMappings.Messages,
};

@Injectable({
	providedIn: 'root',
})
export class AuthStore extends ComponentStore<AuthState> {
	private jade = inject(JadeService);
	private session = inject(SessionService);
	private appState = inject(AppStateService);
	private sessionTimeout = inject(SessionTimeoutService);
	private router = inject(Router);
	private globalStoreRegistry = inject(GlobalStoreRegistryStore);
	private notifications = inject(NotificationsService);
	private userAccess = inject(UserAccessService);
	private menuBuilder = inject(MenuBuilderService);
	private global = inject(Global);
	private eventBus = inject(NgEventBus);

	constructor() {
		super(defaultState);

		this.checkForExistingSessionAtStartup();
		this.sessionTimeout.setAuthStore(this);
	}

	private ready = new BehaviorSubject<boolean>(false);
	readonly ready$ = this.ready.asObservable();

	private readonly loggedOut = new Subject<void>();
	readonly loggedOut$ = this.loggedOut.asObservable();

	private readonly authReset = new Subject<void>();
	readonly authReset$ = this.authReset.asObservable();

	private readonly mfaCodeInvalid = new Subject<void>();
	readonly mfaCodeInvalid$ = this.mfaCodeInvalid.asObservable();

	private readonly totpCodeInvalid = new Subject<void>();
	readonly totpCodeInvalid$ = this.totpCodeInvalid.asObservable();

	private readonly versionMismatch = new Subject<void>();
	readonly versionMismatch$ = this.versionMismatch.asObservable();

	readonly isOrchestrating$ = this.select(({ isOrchestrating }) => !!isOrchestrating);
	private readonly mfa$ = this.select(({ mfa }) => mfa);

	readonly mfaExpirySeconds$ = this.select(this.mfa$, (mfa) => mfa?.expirySeconds ?? 0);
	readonly resendingMfaCode$ = this.select(this.mfa$, (mfa) => !!mfa?.resending);
	readonly validatingMfaCode$ = this.select(this.mfa$, (mfa) => !!mfa?.validating);
	readonly mfaExpiresAt$ = this.select(this.mfa$, (mfa) => {
		if (mfa) {
			const expiresAt = ensureDateTime(mfa.codeGeneratedAt).plus({ seconds: mfa.expirySeconds }).toJSDate();
			return expiresAt;
		}

		return new Date();
	});

	readonly mfaVm$ = this.select({
		expirySeconds: this.mfaExpirySeconds$,
		expiresAt: this.mfaExpiresAt$,
		resending: this.resendingMfaCode$,
		validating: this.validatingMfaCode$,
	});

	readonly totp$ = this.select(({ totp }) => totp);
	readonly validatingTotpCode$ = this.select(this.totp$, (totp) => !!totp?.validating);
	readonly totpSettingUp$ = this.select(this.totp$, (totp) => !!totp?.settingUp);

	readonly totpVm$ = this.select({
		validating: this.validatingTotpCode$,
		settingUp: this.totpSettingUp$,
	});

	readonly setInProgress = this.updater((state, inProgress: boolean) => ({
		...state,
		inProgress,
	}));

	readonly setMessage = this.updater((state, message: string) => ({
		...state,
		message,
	}));

	readonly setLoggingIn = this.effect((value$: Observable<boolean>) =>
		value$.pipe(
			tap((value) => {
				this.patchState({ loggingIn: value });
				if (value) {
					this.patchState({ message: '' });
				}
			}),
		),
	);

	readonly setLoggingOut = this.updater((state, loggingOut: boolean) => ({
		...state,
		loggingOut,
	}));

	readonly setFromICareUserName = this.updater((state, fromICareUserName: string) => ({
		...state,
		fromICareUserName,
	}));

	readonly setPasswordResetId = this.updater((state, passwordResetId: string) => ({
		...state,
		passwordResetId,
	}));

	readonly setIsOrchestrating = this.updater((state, isOrchestrating: boolean) => ({
		...state,
		isOrchestrating,
	}));

	readonly setLoginStage = this.updater((state, loginStage: string) => ({
		...state,
		loginStage,
	}));

	readonly setMultiStaffMasters = this.updater((state, multiStaffMasters: StaffMasterLoginInfoDto[]) => ({
		...state,
		multiStaffMasters,
	}));

	readonly setUiMode = this.updater((state, uiMode: UiMode) => ({
		...state,
		uiMode,
	}));

	readonly setMultiStaffMasterLoginParams = this.updater(
		(state, multiStaffMasterLoginParams: MultiStaffMasterLoginParams | undefined) => ({
			...state,
			multiStaffMasterLoginParams,
		}),
	);

	readonly setCurrentPassword = this.updater((state, currentPassword: string) => ({
		...state,
		multiStaffMasterLoginParams: {
			...state.multiStaffMasterLoginParams,
			password: currentPassword ?? '',
		},
	}));

	readonly chosenStaffMasterOid$ = this.select(({ chosenStaffMasterOid }) => chosenStaffMasterOid);
	readonly redirectAfterLogonUrl$ = this.appState.redirectAfterLogonUrl$;
	readonly inProgress$ = this.select((state) => state.inProgress);
	readonly loggingOut$ = this.select(({ loggingOut }) => !!loggingOut);
	readonly retainMessage$ = this.select(({ retainMessage }) => !!retainMessage);
	readonly suppressCheckUnsavedChanges$ = this.select(
		({ suppressCheckUnsavedChanges }) => !!suppressCheckUnsavedChanges,
	);
	readonly fromICareUserName$ = this.select((state) => state.fromICareUserName);
	readonly passwordResetId$ = this.select((state) => state.passwordResetId);
	readonly mode$ = this.select((state) => state.mode);
	readonly message$ = this.select((state) => state.message);
	readonly uiMode$ = this.select((state) => state.uiMode);
	readonly multiStaffMasters$ = this.select((state) => state.multiStaffMasters);
	readonly multiStaffMasterLoginParams$ = this.select((state) => state.multiStaffMasterLoginParams);
	readonly route$ = this.select((state) => state.route);
	readonly disableUsername$ = this.select((state) => state.inProgress || state.windowsAuthenticationMode);
	readonly modeAction$ = this.select((state) => {
		switch (state.mode) {
			case Modes.ClockIn:
				return 'clock-in';
			case Modes.ClockOut:
				return 'clock-out';
			default:
				return 'login';
		}
	});
	readonly windowsAuthenticationMode$ = this.select((state) => state.windowsAuthenticationMode);
	private readonly changePasswordFromResetParams$ = this.select((state) => state.changePasswordFromResetParams);

	readonly vm$ = this.select({ windowsAuthenticationMode: this.windowsAuthenticationMode$ });

	get loggingOut() {
		const { loggingOut } = this.get();
		return loggingOut;
	}

	get loggingIn() {
		const { loggingIn } = this.get();
		return loggingIn;
	}

	get userName() {
		const { multiStaffMasterLoginParams } = this.get();
		return multiStaffMasterLoginParams?.username;
	}

	/**
	 * Implements logging the user out, sending a message to the server.
	 */
	readonly logOut = this.effect(
		(params$: Observable<{ forced?: boolean; logoutFromTimeout?: boolean; message?: string }>) => {
			return params$.pipe(
				tap(({ message }) => {
					this.setMessage(message ?? '');
					if (message) {
						this.patchState({
							retainMessage: true,
						});
					}
				}),
				switchMap(({ forced, logoutFromTimeout, message }) => {
					if (forced) {
						this.setAuthenticated(false);
						this.checkForLoginRouteAndRouteIfNotThere();
						return of(null);
					}

					if (logoutFromTimeout) {
						this.patchState({
							suppressCheckUnsavedChanges: true,
						});
					}

					this.reset();

					// Set the retain message flag again so the message isn't removed on returning to login.
					if (message) {
						this.setMessage(message ?? '');
						this.patchState({
							retainMessage: true,
						});
					}

					this.setLoggingOut(true);
					return this.jade
						.post({
							$type: Types.ApiLogoutRequest,
						})
						.pipe(
							// Must catch the error otherwise the effect will be cancelled, never to work again!
							catchError(() => {
								// Do nothing if the logout request fails.
								return EMPTY;
							}),
							finalize(() => {
								this.setLoggingOut(false);
								this.setAuthenticated(false);
								this.appState.setNotAuthenticated();

								this.checkForLoginRouteAndRouteIfNotThere();
							}),
						);
				}),
			);
		},
	);

	readonly setWindowsAuthentication = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({
					windowsAuthenticationMode: true,
				});
			}),
		),
	);

	readonly clearWindowsAuthentication = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({
					windowsAuthenticationMode: false,
				});
			}),
		),
	);

	private readonly checkForLoginRouteAndRouteIfNotThere = this.effect((trigger$) =>
		trigger$.pipe(
			delay(0),
			tap(() => {
				if (this.router.routerState.snapshot.url !== '/login') {
					this.router.navigate(['/login']);
				}
			}),
		),
	);

	/**
	 * Forces the user back to login.
	 */
	readonly forceLogin = this.effect((trigger$) => {
		return trigger$.pipe(
			tap(() => {
				this.setLoggingOut(true);
				this.setAuthenticated(false);
				this.router.navigate(['/login']);
				this.reset();
			}),
		);
	});

	/**
	 * Attempts to log the user in. This is the main authentication pathway.
	 * It may then force the user to change their password, or select one of multiple staff masters to log in as,
	 * or (obviously) reject the login if they got their password wrong, or they're locked out.
	 */
	readonly attemptLogin = this.effect((params$: Observable<AuthenticateParams>) => {
		return params$.pipe(
			tap((params) => {
				this.setInProgress(true);
				this.setLoggingOut(false);
				this.setLoggingIn(true);
				this.setMessage('');

				this.patchState({
					route: params.route,
					mode: params.mode,
					multiStaffMasterLoginParams: {
						username: params.username,
						password: params.password,
						rememberMe: params.rememberUserName,
					},
				});
			}),
			withLatestFrom(this.passwordResetId$, this.fromICareUserName$, this.windowsAuthenticationMode$),
			switchMap(([params, passwordResetId, fromICareUserName, windowsAuthenticationMode]) => {
				if (params.mode === Modes.LoginICare && fromICareUserName) {
					params.username = fromICareUserName;
					this.setFromICareUserName('');
				}

				if (params.mode === Modes.ResetPassword && passwordResetId) {
					this.setPasswordResetId('');
				}

				const loginParams = {
					$type: Types.ApiLoginRequest,
					username: params.username,
					password: params.password,
					mode: params.mode,
					windowsAuthenticationMode,
					...params.additionalData,
				} as ApiLoginRequest;

				return this.jade.post<ApiLoginResult>(loginParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);
							this.authSucceeded(result);

							if (result.result === ApiLoginRequest_Constants.Result_MFA_NoEmailAddress) {
								this.setMessage(
									"Login failed. You don't have an email address set up for MFA. Please contact your administrator.",
								);
								return;
							}

							if (
								result.result === ApiLoginRequest_Constants.Result_Login_OK &&
								params.mode === Modes.Login
							) {
								this.appState.updateDefaultUsername(
									params.rememberUserName ? params.username ?? '' : '',
								);
							}
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									if (typeof err.error === 'object') {
										const errorResult = err.error as ApiErrorResult;
										errorMessage = errorResult.message;
									} else if (err.status === 503 || err.status === 504) {
										errorMessage = 'Server is unavailable';
									}
								}
							}

							this.setMessage(errorMessage);
						},
					),
				);
			}),
		);
	});

	/**
	 * Continues login/clock-in/clock-out with a selected (multiple) staff master.
	 */
	readonly loginAsStaffMaster = this.effect((staffMaster$: Observable<StaffMasterLoginInfoDto>) => {
		return staffMaster$.pipe(
			tap(() => {
				this.setInProgress(true);
			}),
			withLatestFrom(this.multiStaffMasterLoginParams$, this.mode$),
			switchMap(([master, params, mode]) => {
				const loginParams: ApiLoginRequest = {
					$type: Types.ApiLoginRequest,
					ancillaryMode: ApiLoginRequest_Constants.AncillaryMode_ChooseStaffMaster,
					mode,
					myStaffMaster: master.oid,
					password: params?.password,
					username: params?.username,
				} as ApiLoginRequest;

				// Record the chosen StaffMaster's oid for use if TOTP is required.
				this.patchState({
					chosenStaffMasterOid: master.oid,
				});

				this.setLoggingIn(true);

				return this.jade.post<ApiLoginResult>(loginParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);
							this.authSucceeded(result);

							if (result.result === ApiLoginRequest_Constants.Result_Login_OK) {
								this.appState.updateDefaultUsername(params?.rememberMe ? params.username ?? '' : '');
							}
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									const errorResult = err.error as ApiErrorResult;
									errorMessage = errorResult.message;
								}
							}

							this.setMessage(errorMessage);
						},
					),
				);
			}),
		);
	});

	readonly resendMultiFactorAuthenticationCode = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.mfa$),
			exhaustMap(([, mfa]) => {
				assertDefined(mfa);

				mfa.resending = true;

				this.patchState({
					mfa: { ...mfa },
				});

				const params = {
					$type: Types.ApiResendMultiFactorAuthenticationCodeRequest,
					oid: mfa.accessCodeOid,
				} as ApiResendMultiFactorAuthenticationCodeRequest;

				return this.jade.post<ApiLoginResult>(params).pipe(
					tapResponse(
						(result) => {
							mfa.expirySeconds = result.mfaExpirySeconds;
							mfa.key = result.mfaKey;
							mfa.codeGeneratedAt = new Date();

							this.patchState({
								mfa: { ...mfa },
							});

							this.mfaCodeInvalid.next();

							this.setMessage('A new code was sent.');
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									if (typeof err.error === 'object') {
										const errorResult = err.error as ApiErrorResult;
										errorMessage = errorResult.message;
									} else if (err.status === 503 || err.status === 504) {
										errorMessage = 'Server is unavailable';
									}
								}
							}

							this.setUiMode(UiMode.Login);
							this.setMessage(errorMessage);
						},
					),
					finalize(() => this.patchState({ mfa: { ...mfa, resending: false } })),
				);
			}),
		),
	);

	readonly completeMfaLogin = this.effect((enteredCode$: Observable<string>) =>
		enteredCode$.pipe(
			withLatestFrom(this.mfa$, this.multiStaffMasterLoginParams$),
			exhaustMap(([enteredCode, mfa, params]) => {
				assertDefined(mfa);
				assertDefined(params);

				this.setLoggingIn(true);

				this.patchState({
					mfa: { ...mfa, validating: true },
				});

				const loginParams: ApiLoginRequest = {
					$type: Types.ApiLoginRequest,
					mode: ApiLoginRequest_Constants.Mode_Login,
					mfaCode: enteredCode,
					mfaId: mfa.key,
				} as ApiLoginRequest;

				return this.jade.post<ApiLoginResult>(loginParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);
							this.authSucceeded(result);

							if (params.rememberMe && result.result === ApiLoginRequest_Constants.Result_Login_OK) {
								this.appState.updateDefaultUsername(params.username ?? '');
							}

							if (result.result === ApiLoginRequest_Constants.Result_InvalidMfaCode) {
								this.setMessage('Invalid MFA code');

								this.mfaCodeInvalid.next();
							}
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									const errorResult = err.error as ApiErrorResult;
									errorMessage = errorResult.message;
								}
							}

							this.setMessage(errorMessage);
							this.setUiMode(UiMode.Login);
						},
					),
					finalize(() => {
						this.patchState({ mfa: { ...mfa, validating: false } });
						this.setLoggingIn(false);
					}),
				);
			}),
		),
	);

	readonly completeTotpLogin = this.effect((enteredCode$: Observable<string>) =>
		enteredCode$.pipe(
			withLatestFrom(this.totp$, this.multiStaffMasterLoginParams$, this.chosenStaffMasterOid$),
			exhaustMap(([enteredCode, totp, params, chosenStaffMasterOid]) => {
				assertDefined(totp);
				assertDefined(params);

				this.setLoggingIn(true);

				this.patchState({
					totp: { ...totp, validating: true },
				});

				const loginParams: ApiLoginRequest = {
					$type: Types.ApiLoginRequest,
					mode: ApiLoginRequest_Constants.Mode_Login,
					totpCode: enteredCode,
					username: params.username,
					password: params.password,
				} as ApiLoginRequest;

				if (chosenStaffMasterOid) {
					loginParams.myStaffMaster = chosenStaffMasterOid;
					loginParams.ancillaryMode = ApiLoginRequest_Constants.AncillaryMode_ChooseStaffMaster;
				}

				if (params.changingPasswordFromReset) {
					loginParams.mode = ApiLoginRequest_Constants.Mode_ResetPassword;
					loginParams.ancillaryMode = ApiLoginRequest_Constants.AncillaryMode_ChangePassword;
					loginParams.password = '';
					loginParams.newPassword = params.password ?? '';
				} else if (params.changingPasswordFromExpiry) {
					loginParams.mode = ApiLoginRequest_Constants.Mode_Login;
					loginParams.ancillaryMode = ApiLoginRequest_Constants.AncillaryMode_ChangePassword;
					loginParams.password = params.password ?? '';
					loginParams.newPassword = params.newPassword ?? '';
				}

				return this.jade.post<ApiLoginResult>(loginParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);

							if (result.result === ApiLoginRequest_Constants.Result_InvalidTotpCode) {
								this.setMessage('Invalid code');

								this.totpCodeInvalid.next();
								return;
							}

							this.authSucceeded(result);
							this.updateDefaultUserNameIfRemembered();

							if (result.result === ApiLoginRequest_Constants.Result_MultiStaffMaster) {
								this.eventBus.cast(
									EventBusEvents.Event_ShowSnackbar_Message,
									`${params.username} - verified`,
								);
							}
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									const errorResult = err.error as ApiErrorResult;
									errorMessage = errorResult.message;
								}
							}

							this.setMessage(errorMessage);
							this.setUiMode(UiMode.Login);
						},
					),
					finalize(() => {
						this.patchState({ totp: { ...totp, validating: false } });
						this.setLoggingIn(false);
					}),
				);
			}),
		),
	);

	readonly totpSetupCompleted = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({
					totp: {
						settingUp: false,
					},
				});

				this.ready.next(true);
			}),
			delay(0),
			tap(() => {
				this.nextOrchestratedScreen();
			}),
		),
	);

	readonly updateDefaultUserNameIfRemembered = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.multiStaffMasterLoginParams$),
			tap(([, params]) => {
				assertDefined(params);

				if (!params.rememberMe) {
					return;
				}

				this.appState.updateDefaultUsername(params.username ?? '');
			}),
		),
	);

	/**
	 * Sends a password change to the server.
	 */
	readonly changePassword = this.effect((params$: Observable<ChangePasswordParameters>) => {
		return params$.pipe(
			tap(() => {
				this.setInProgress(true);
			}),
			withLatestFrom(this.multiStaffMasterLoginParams$),
			switchMap(([params, multiParams]) => {
				this.setLoggingIn(true);

				const loginParams: ApiLoginRequest = {
					$type: Types.ApiLoginRequest,
					ancillaryMode: ApiLoginRequest_Constants.AncillaryMode_ChangePassword,
					mode: ApiLoginRequest_Constants.Mode_Login,
					password: params.oldPassword,
					username: params.username,
					newPassword: params.newPassword,
				} as ApiLoginRequest;

				return this.jade.post<ApiLoginResult>(loginParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);
							this.authSucceeded(result);

							this.setCurrentPassword(params.newPassword);

							if (result.result === ApiLoginRequest_Constants.Result_TOTP) {
								this.patchState({
									multiStaffMasterLoginParams: {
										username: params.username,
										password: params.oldPassword,
										newPassword: params.newPassword,
										changingPasswordFromExpiry: true,
									},
								});
							} else if (result.result === ApiLoginRequest_Constants.Result_Login_OK) {
								this.appState.updateDefaultUsername(
									multiParams?.rememberMe ? params.username ?? '' : '',
								);
							}
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									const errorResult = err.error as ApiErrorResult;
									errorMessage = errorResult.message;
								}
							} else if (err instanceof BusinessError) {
								errorMessage = err.message;
							}

							this.setMessage(errorMessage);
						},
					),
				);
			}),
		);
	});

	readonly changePasswordFromReset = this.effect((params$: Observable<ChangePasswordFromResetCompleteParameters>) => {
		return params$.pipe(
			tap(() => {
				this.setInProgress(true);
			}),
			withLatestFrom(this.changePasswordFromResetParams$),
			switchMap(([params, changePasswordParams]) => {
				assertDefined(changePasswordParams);
				this.setLoggingIn(true);

				this.patchState({
					mode: Modes.Login,
					route: params.route,
				});

				const loginParams: ApiLoginRequest = {
					$type: Types.ApiLoginRequest,
					ancillaryMode: ApiLoginRequest_Constants.AncillaryMode_ChangePassword,
					mode: ApiLoginRequest_Constants.Mode_ResetPassword,
					username: changePasswordParams.id,
					newPassword: params.newPassword,
				} as ApiLoginRequest;

				return this.jade.post<ApiLoginResult>(loginParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);
							this.authSucceeded(result);

							if (result.result === ApiLoginRequest_Constants.Result_TOTP) {
								this.patchState({
									multiStaffMasterLoginParams: {
										username: changePasswordParams.id,
										password: params.newPassword,
										changingPasswordFromReset: true,
									},
								});
							}
						},
						(err) => {
							this.setLoggingIn(false);
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									const errorResult = err.error as ApiErrorResult;
									errorMessage = errorResult.message;
								}
							}

							this.setMessage(errorMessage);
						},
					),
				);
			}),
		);
	});

	readonly resetPassword = this.effect((params$: Observable<ResetPasswordParameters>) => {
		return params$.pipe(
			tap(() => {
				this.setInProgress(true);
			}),
			switchMap((params) => {
				const resetParams: ApiResetPasswordRequest = {
					$type: Types.ApiResetPasswordRequest,
					username: params.username,
					emailAddress: params.emailAddress,
					isNg: true,
				} as ApiResetPasswordRequest;

				return this.jade.post<ApiLoginResult>(resetParams).pipe(
					tapResponse(
						(result) => {
							this.setInProgress(false);

							if (result.result === ApiResetPasswordRequest_Constants.Result_InvalidEmail) {
								this.setMessage(
									'Invalid email address for your username. Please re-enter email address or contact your administrator to get set up.',
								);
								return;
							}

							if (result.result === ApiResetPasswordRequest_Constants.Result_EmailSent) {
								this.reset();
								this.setMessage('Password reset email sent.');
							}
						},
						(err) => {
							this.setAuthenticated(false);
							this.setInProgress(false);

							let errorMessage = 'Login error';

							if (err instanceof HttpErrorResponse) {
								if (err.error) {
									const errorResult = err.error as ApiErrorResult;
									errorMessage = errorResult.message;
								}
							}

							this.setMessage(errorMessage);
						},
					),
				);
			}),
		);
	});

	readonly setMessageRetained = this.effect((message$: Observable<string>) =>
		message$.pipe(
			tap((message) => {
				this.patchState({
					message,
					retainMessage: true,
				});
			}),
		),
	);

	readonly reset = this.effect((trigger$) => {
		return trigger$.pipe(
			withLatestFrom(this.retainMessage$),
			tap(([, retainMessage]) => {
				this.setLoggingIn(false);
				this.setLoggingOut(false);
				this.setLoginStage('');
				this.setMultiStaffMasterLoginParams(undefined);
				if (!retainMessage) {
					this.setMessage('');
				}
				this.setUiMode(UiMode.Login);
				this.patchState({
					retainMessage: false,
					mfa: undefined,
					totp: undefined,
					chosenStaffMasterOid: undefined,
				});

				this.globalStoreRegistry.reset();
				this.authReset.next();

				this.router.navigate(['/login', { outlets: { aux: [] } }]);
			}),
		);
	});

	readonly completeClocking = this.effect((params$: Observable<CompleteClockingParameters>) => {
		return params$.pipe(
			tap((params) => {
				this.reset();

				const mode = params.mode === Modes.ClockIn ? 'Clock-in' : 'Clock-out';
				this.setMessage(`${mode} completed for ${params.staffName}`);
			}),
		);
	});

	readonly initiatePasswordReset = this.effect((resetKey$: Observable<string>) => {
		return resetKey$.pipe(
			tap((resetKey) => {
				const params = this.decodePasswordResetId(resetKey);
				if (params) {
					this.patchState({
						changePasswordFromResetParams: params,
						multiStaffMasterLoginParams: {
							username: params.username,
						},
					});
					this.setUiMode(UiMode.ChangePasswordFromReset);
				}
			}),
		);
	});

	readonly initiateLoginFromICare = this.effect((iCareSessionId$: Observable<string>) => {
		return iCareSessionId$.pipe(
			tap((iCareSessionId) => {
				this.setUiMode(UiMode.LoginFromICare);

				this.setFromICareUserName(iCareSessionId);
				this.attemptLogin({
					mode: Modes.LoginICare,
				} as AuthenticateParams);
			}),
		);
	});

	readonly initiateWindowsAuthenticationMode = this.effect((trigger$) => {
		return trigger$.pipe(
			tap(() => {
				this.patchState({
					windowsAuthenticationMode: true,
				});
			}),
		);
	});

	private readonly navigateToClocking = this.effect((route$: Observable<ActivatedRoute>) => {
		return route$.pipe(
			withLatestFrom(this.mode$),
			tap(([route, mode]) => {
				const primaryRouteUrl = this.router
					.createUrlTree(['.'], { relativeTo: route })
					.root.children[PRIMARY_OUTLET].toString();

				this.router.navigate([
					primaryRouteUrl,
					{ outlets: { aux: ['clocking', mode === Modes.ClockIn ? 'in' : 'out'] } },
				]);
			}),
		);
	});

	/**
	 * Handles the user being successfully logged in.
	 */
	private readonly authSucceeded = this.effect((result$: Observable<ApiLoginResult>) => {
		return result$.pipe(
			withLatestFrom(this.mode$, this.route$, this.redirectAfterLogonUrl$),
			tap(([result, mode, route, redirectAfterLogonUrl]) => {
				this.patchState({
					loginStage: '',
				});

				let hasLoginStage = false;
				let signalReady = true;

				// Compare the client version with the version from the server. If they're different, the client is out of date.
				// This will trigger the login page to show a message and force page reload after a period.
				if (result.versionInfo !== this.global.appVersion) {
					this.setAuthenticated(false);
					this.versionMismatch.next();
					return;
				}

				if (
					mode === Modes.Login ||
					mode === Modes.ClockIn ||
					mode === Modes.ClockOut ||
					mode === Modes.LoginICare ||
					mode == Modes.ResetPassword
				) {
					switch (result.result) {
						case ApiLoginRequest_Constants.Result_MultiStaffMaster:
							this.setMultiStaffMasters(result.allStaffMasters);
							this.setUiMode(UiMode.SelectStaffMaster);
							hasLoginStage = true;
							break;

						case ApiLoginRequest_Constants.Result_PasswordExpired:
							this.setUiMode(UiMode.ChangePassword);
							hasLoginStage = true;
							break;

						case ApiLoginRequest_Constants.Result_TwoFactorChallenge:
							// Not implemented
							hasLoginStage = true;
							break;
						case ApiLoginRequest_Constants.Result_PasswordChanged:
							// Back to login
							this.setUiMode(UiMode.Login);
							this.setMessage('Password changed. Please log in with your new password.');
							hasLoginStage = true;
							break;
						case ApiLoginRequest_Constants.Result_MFA:
							this.setUiMode(UiMode.MultiFactorAuthentication);
							hasLoginStage = true;

							this.patchState({
								mfa: {
									codeGeneratedAt: new Date(),
									key: result.mfaKey,
									expirySeconds: result.mfaExpirySeconds,
									accessCodeOid: result.myAccessCode,
								},
							});
							break;
						case ApiLoginRequest_Constants.Result_TOTP:
							this.setUiMode(UiMode.TotpAuthentication);
							hasLoginStage = true;

							this.patchState({
								totp: {
									validating: false,
								},
							});
							break;
						case ApiLoginRequest_Constants.Result_TOTP_Setup: {
							// User is required to set up TOTP
							const mutable = result as Mutable<ApiLoginResult>;
							mutable.allOrchestrationItems = ['authenticator-setup'];

							this.patchState({
								totp: {
									settingUp: true,
								},
							});
							signalReady = false;
							break;
						}
						case ApiLoginRequest_Constants.Result_TOTP_Setup_Required:
							this.setUiMode(UiMode.Login);
							this.setMessage(
								'MFA setup is required. Please log in and complete setup before using clock-in/clock-out.',
							);
							hasLoginStage = true;
							break;
					}
				}

				if (hasLoginStage) {
					this.setLoginStage(result.result);
				} else {
					switch (mode) {
						case Modes.Login:
						case Modes.LoginICare: {
							const params: LoginCompleteParams = {
								jwt: result.jwt,
								menuItems: result.menuItems,
								securityItems: result.securityItems,
								editorItems: result.editorItems,
								options: result.options,
							};

							this.loginComplete(params);

							break;
						}

						default: {
							const params: LoginCompleteParams = {
								jwt: result.jwt,
								menuItems: [],
								securityItems: [],
								editorItems: [],
								options: result.options,
							};

							this.loginComplete(params);
						}
					}

					if (signalReady) {
						this.ready.next(true);
					}

					// TODO: Raygun config

					// window.rg4js('setUser', {
					// 	identifier: this.Settings.identifier,
					// 	email: this.Settings.email,
					// 	firstName: this.Settings.firstName,
					// 	fullName: this.Settings.staffName
					// });
					// window.rg4js('setVersion', result.versionInfo);

					this.session.authMode = mode as Modes;

					// TODO: Handle states

					/*				
						 let currentState = this.$state.current.name || 'home';
			
						// Allow access to the following state(s) directly from the URL upon logging in.
						const whiteListedStates = {
							'staff.staff-alerts': true,
							'staff.staff-messages': true
						};
			
						const isWhiteListedState = whiteListedStates[currentState];
						const hasAccess = this.hasAccessToState(currentState);
			
						if (!isWhiteListedState && !hasAccess) {
							// Ensure we're in the home state upon logging in.
							this.$state.go('home');
						}
			*/

					// const mapping = getSecurityMapping('alerts', this.appState);
					// const access = this.userAccess.canAccessControlPath(mapping);

					const redirectSecurityKey = whiteListRoutes[redirectAfterLogonUrl];

					const isWhiteListedState = !!redirectSecurityKey;
					let hasAccess = false;

					if (isWhiteListedState) {
						hasAccess = this.userAccess.canAccessControlPath(redirectSecurityKey);
					}

					switch (mode) {
						case Modes.ClockIn:
						case Modes.ClockOut:
							this.setAuthenticated(true);
							route && this.navigateToClocking(route);
							break;
						case Modes.Login:
						case Modes.LoginICare:
						case Modes.ResetPassword: {
							// Build the orchestration screens
							let orchestrationItems: string[] = [];
							if (isWhiteListedState && hasAccess) {
								orchestrationItems = [redirectSecurityKey];
							}

							orchestrationItems = [...(result.allOrchestrationItems || []), ...orchestrationItems];

							if (result.defaultScreenName) {
								orchestrationItems.push(result.defaultScreenName);
							}

							this.session.setOrchestrationItems(orchestrationItems);
							this.setAuthenticated(true);
							break;
						}
					}
				}
			}),
		);
	});

	private readonly setAuthenticated = this.effect((authenticated$: Observable<boolean>) => {
		return authenticated$.pipe(
			withLatestFrom(this.mode$),
			tap(([authenticated, mode]) => {
				this.setLoggingIn(false);

				this.patchState({
					suppressCheckUnsavedChanges: false,
				});

				if (authenticated) {
					switch (mode) {
						case Modes.Login:
						case Modes.LoginICare:
							this.appState.setRedirectAfterLogonUrl('');
							this.nextOrchestratedScreen();

							this.setUpNotifications();
							break;
					}
				} else {
					this.loggedOut.next();
					this.setIsOrchestrating(false);

					this.session.end();
					this.ready.next(false);
					this.appState.setNotAuthenticated();
				}
			}),
		);
	});

	private readonly loginComplete = this.effect((params$: Observable<LoginCompleteParams>) => {
		return params$.pipe(
			tap((params) => {
				const securityMap = buildSecurityMapFromMenuItems(params.menuItems || []);
				if (params.securityItems) {
					addSecurityItemsToSecurityMap(securityMap, params.securityItems);
				}

				const flatMenuItems = flattenMenuItems(params.menuItems || []);
				const editorMap = buildEditorMap(params.editorItems || []);

				this.appState.onAuthenticated({
					jwt: params.jwt,
					securityMap,
					flatMenuItems,
					editorMap,
					options: params.options,
				});

				this.session.start(false);
			}),
		);
	});

	private readonly setUpNotifications = this.effect((trigger$) =>
		trigger$.pipe(
			exhaustMap(() => {
				return this.notifications.beginSessionNotification<ApiSessionForceRemoveNotification>(ForceRemove).pipe(
					takeUntil(
						// Stop listening if the session has ended, or they've logged out, or they're in the processing of logging out.
						merge(
							this.sessionTimeout.sessionEnded$,
							this.loggedOut$,
							this.loggingOut$.pipe(filter((v) => !!v)),
						),
					),
					withLatestFrom(this.sessionTimeout.forceEnd$),
					tap(([, forceEnd]) => {
						if (forceEnd) {
							// If already forcing end, due to the timeout dialog, don't replace the message.
							return;
						}

						this.logOut({ forced: true, message: 'Session forced off' });
					}),
				);
			}),
		),
	);

	private decodePasswordResetId(passwordResetId: string): ChangePasswordFromResetParameters | null {
		let converted: string;

		try {
			converted = window.atob(passwordResetId);
		} catch (err) {
			return null;
		}
		const parts = converted.split('|');
		if (parts.length !== 3) {
			return null;
		}

		const len = Number(parts[0]);
		const un = parts[1];
		const id = parts[2];

		if (un.length !== len) {
			return null;
		}

		return {
			username: un,
			id,
		};
	}

	private checkForExistingSessionAtStartup() {
		// Need to do this in a timeout so that it runs after DI has resolved to prevent a circular dependency with the HTTP interceptors...
		setTimeout(() => {
			this.checkLogin();
		});
	}

	private checkLogin() {
		const jwt = this.appState.jwt;
		if (!jwt) {
			return;
		}

		// Fire off a request to the server to check the JWT is still valid.
		const param: ApiLoginCheckRequest = {
			$type: Types.ApiLoginCheckRequest,
		};

		this.jade
			.post<string>(param)
			.pipe(
				catchError(() => {
					this.appState.setNotAuthenticated();
					return EMPTY;
				}),
				tap((newJwt) => {
					this.appState.updateJwt(newJwt);
					this.sessionTimeout.startSession();
					this.ready.next(true);
				}),
			)
			.subscribe();
	}

	readonly nextOrchestratedScreen = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				const items = this.session.orchestrationItems;
				const nextItem = items.shift();
				if (nextItem) {
					// Special case for authenticator setup. It's not in the menu structure.
					if (nextItem === 'authenticator-setup') {
						this.router.navigate(['/']).then(() => {
							this.router.navigate([{ outlets: { aux: ['authenticator-setup'] } }], { replaceUrl: true });
						});
						return;
					}

					const menu = this.menuBuilder.getMenuItem(nextItem);

					if (menu) {
						this.setIsOrchestrating(true);
						this.router.navigate([menu.route]);
					} else {
						this.setIsOrchestrating(false);
						this.router.navigate(['/']);
					}
				} else {
					this.setIsOrchestrating(false);
					this.router.navigate(['/']);
				}
			}),
		),
	);
}
